import type {ReactElement} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize} from '@Components/icon-v2/icon.types';
import {useFavoriteHeart} from '@Panels/stock-panel/components/favorite-button/use-favorite-heart';
import {areAllStockItemsFavorited, countStockItemsNotFavorited, isAnyStockFavoriteTogglePending} from '@Panels/stock-panel/stock-favorites-reducer';
import {toggleStockFavoritesTogether} from '@Panels/stock-panel/stock-favorites-thunk';
import {getStockFavoriteIdentities} from '@Panels/stock-panel/stock-favorites.library';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {FavoriteSelectionButtonProps} from './favorite-selection-button.types';
import styles from './favorite-selection-button.module.scss';

/**
 * The heart in the multi-select footer. A phone has no hover to reveal a tile's heart, so the selection is
 * favorited as one, and the heart is filled only when every selected item is a favorite. Tiles that cannot be
 * favorited are left out; when none of the selection can be, there is no heart.
 */
export function FavoriteSelectionButton({selectedItems}: FavoriteSelectionButtonProps): ReactElement | null {
  const dispatch = useAppDispatch();
  const identities = getStockFavoriteIdentities(selectedItems);
  const isEverySelectedItemFavorited = useAppSelector((state) => areAllStockItemsFavorited(state, identities));
  const isTogglePending = useAppSelector((state) => isAnyStockFavoriteTogglePending(state, identities));
  const notFavoritedItemCount = useAppSelector((state) => countStockItemsNotFavorited(state, identities));
  const heart = useFavoriteHeart({
    isFavorited: isEverySelectedItemFavorited,
    isTogglePending,
    addedItemCount: notFavoritedItemCount,
    requestToggle: () => dispatch(toggleStockFavoritesTogether(identities)),
  });

  if (heart === null || identities.length === 0) {
    return null;
  }

  return (
    <div className={styles.anchor}>
      <Icon
        icon={heart.heartIcon}
        shape={IconShape.SQUARE}
        size={IconSize.SIZE_ICON_20}
        className={`${styles.button} ${isEverySelectedItemFavorited ? styles.favorited : ''}`}
        isUsedAsButton
        ariaLabel={heart.accessibleLabel}
        onClick={heart.toggleHeart}
      />
    </div>
  );
}
